import CertificationProgress from "../CertificationProgress";
import React, { useEffect, useRef } from "react";
import partition from "lodash/partition";
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  DarkMode,
  Flex,
  Heading,
  keyframes,
  useColorModeValue,
  usePrefersReducedMotion,
} from "@chakra-ui/react";
import {
  CONFETTI_CERTIFIED_COLORS,
  CONFETTI_COLORS,
  OdysseyUser,
  updateCertifications,
  useCertificationConfetti,
  useCertificationReport,
} from "@utils";
import { CertificationBadge } from "@components/CertificationBadge";
import { FiArrowRight } from "@react-icons/all-files/fi/FiArrowRight";
import { Link as GatsbyLink } from "gatsby";
import { useCreateCertificationMutation } from "@graphql";

export const glare = keyframes({
  to: {
    backgroundPosition: "100%",
  },
});

const slideFade = keyframes({
  from: {
    opacity: 0,
    transform: "translateY(12px)",
  },
  to: {
    opacity: 1,
    transform: "none",
  },
});

const fade = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
});

const MODAL_DELAY = 250;
const FADE_DURATION = 500;

export type CertificationStatusProps = {
  user: OdysseyUser;
  certification: {
    id: string;
    image: string;
    title: string | null;
    criteria: readonly string[];
    courses: ReadonlyArray<Pick<Queries.Course, "slug" | "progressId">>;
  };
};

export default function CertificationStatus({
  user,
  certification,
}: CertificationStatusProps) {
  const bgColor = useColorModeValue("gray.800", "gray.600");
  const textColor = useColorModeValue("whiteAlpha.900", undefined);
  const prefersReducedMotion = usePrefersReducedMotion();

  const { id: certId, image, title, criteria, courses } = certification;
  const userCourses = user.courses ?? [];
  const completedCourses = userCourses
    .filter((course) => course.completedAt)
    .map((course) => course.id);

  const [completedCriteria, [inProgressCourse]] = partition(courses, (course) =>
    completedCourses.includes(course.progressId)
  );

  const isComplete = completedCriteria.length === criteria.length;
  const savedCert = user.certifications?.find(
    ({ certificationId }) => certificationId === certId
  );

  const sendCertificationReport = useCertificationReport();

  const [createCertification, { loading }] = useCreateCertificationMutation({
    variables: {
      userId: user.id,
      certificationId: certId,
      source: "course",
    },
    update: updateCertifications,
    onCompleted: (data) => {
      sendCertificationReport({
        user,
        title,
        data,
      });
    },
  });

  useEffect(() => {
    // need to check if user has already previously completed the cert before calling mutation
    if (isComplete && !savedCert && !loading) {
      createCertification();
    }
  }, [isComplete, savedCert, loading, createCertification]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  useCertificationConfetti(canvasRef, {
    colors: isComplete ? CONFETTI_CERTIFIED_COLORS : CONFETTI_COLORS,
  });

  const slideFadeStyles = {
    animationFillMode: "backwards",
    animationName: !prefersReducedMotion ? slideFade.toString() : undefined,
    animationDuration: FADE_DURATION + "ms",
  };

  return (
    <DarkMode>
      <Center
        roundedBottomLeft={{ md: "md" }}
        roundedTopRight={{ base: "md", md: "none" }}
        roundedTopLeft="md"
        bgColor={bgColor}
        color={textColor}
        pos="relative"
        p={{ base: 6, md: 0 }}
      >
        <canvas
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          ref={canvasRef}
        />
        <Flex align="center" direction="column" pos="relative">
          <Heading
            mb={{ base: 4, md: 8 }}
            size="lg"
            css={{
              animationFillMode: "backwards",
              animationName: !prefersReducedMotion
                ? fade.toString()
                : undefined,
              animationDuration: FADE_DURATION + "ms",
              animationDelay: MODAL_DELAY + "ms",
            }}
          >
            Certification {isComplete ? "achieved!" : "progress"}
          </Heading>
          <Box w="full" maxW="3xs" textAlign="center" mb={{ base: 4, md: 6 }}>
            <CertificationBadge
              image={image}
              alt={`${title} badge`}
              delay={MODAL_DELAY + FADE_DURATION + "ms"}
              boxSize={{ base: 24, md: 40 }}
              mb="4"
              mx="auto"
              css={{
                ...slideFadeStyles,
                animationDelay: MODAL_DELAY + "ms",
              }}
            />
            <CertificationProgress
              isAnimated={!prefersReducedMotion}
              animationDuration={FADE_DURATION}
              animationDelay={MODAL_DELAY + FADE_DURATION * 2}
              completed={completedCriteria.length}
              total={criteria.length}
              css={{
                ...slideFadeStyles,
                animationDelay: MODAL_DELAY + FADE_DURATION + "ms",
              }}
            />
          </Box>
          <ButtonGroup
            colorScheme="indigo"
            size="lg"
            css={{
              ...slideFadeStyles,
              animationDelay: MODAL_DELAY + FADE_DURATION * 2 + "ms",
            }}
          >
            {savedCert || isComplete ? (
              <Button
                isLoading={loading}
                as={GatsbyLink}
                to={"/certifications/" + savedCert?.id}
              >
                View certification
              </Button>
            ) : (
              <Button
                as={GatsbyLink}
                to={"/" + inProgressCourse.slug}
                variant="ghost"
                rightIcon={<FiArrowRight />}
                onClick={() =>
                  window.gtag?.("event", "next_course_button_click")
                }
              >
                Next course
              </Button>
            )}
          </ButtonGroup>
        </Flex>
      </Center>
    </DarkMode>
  );
}
